import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import {
  SketchLayers,
  sketchHostClass,
  type SketchTone,
} from '@/components/ui/sketch-frame'
import { cn } from '@/lib/utils'

const buttonGlow =
  'transition duration-200 hover:shadow-[0_0_7px_0_--theme(--color-white/20%),0_0_14px_1px_--theme(--color-white/10%)]'

const brandGlow =
  'transition duration-200 hover:shadow-[0_0_7px_0_--theme(--color-purple-200/45%),0_0_14px_1px_--theme(--color-purple-200/22%)]'

const buttonVariants = cva(
  `cursor-pointer inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background ${buttonGlow} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50`,
  {
    variants: {
      variant: {
        default: 'bg-default-100 text-default-500 hover:bg-default-100/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        primary:
          'rounded-xl font-sans bg-black-600 text-white/75 hover:bg-black-600/90 shadow-[inset_0_1px_0_0_--theme(--color-white/10%)]',
        accent:
          'rounded-xl font-sans bg-primary text-primary-foreground/75 hover:bg-primary/90 shadow-[inset_0_1px_0_0_--theme(--color-white/15%)]',
        transparent:
          'bg-transparent font-sans text-default-500/75 hover:text-default-400',
        hand: cn(sketchHostClass, 'font-gochi text-white', brandGlow),
        // Hand-drawn skins (Figma): fill + border come from SketchLayers, the button is only the host box.
        brand: cn(sketchHostClass, 'font-gochi text-white hover:opacity-90', brandGlow),
        muted: cn(sketchHostClass, 'font-gochi text-white', buttonGlow),
      },
      size: {
        default: 'h-11 px-3 py-3 text-lg',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'size-9 p-0 [--sketch-cap:8px]',
        'icon-lg': 'size-11 shrink-0 p-0 [--sketch-cap:8px]',
        sketch: 'h-11 px-2 text-[18px] leading-none [--sketch-cap:8px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    loading?: boolean
  }

type ButtonVariant = NonNullable<ButtonProps['variant']>

const sketchTone: Partial<Record<ButtonVariant, SketchTone>> = {
  hand: 'brand',
  muted: 'muted',
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      children,
      ...props
    },
    ref,
  ) => {
    const tone = sketchTone[variant ?? 'default']
    const classNames = cn(buttonVariants({ variant, size }), className)

    if (asChild) {
      if (!tone) {
        return (
          <Slot className={classNames} ref={ref} {...props}>
            {children}
          </Slot>
        )
      }

      const child = React.Children.only(children) as React.ReactElement<{
        children?: React.ReactNode
      }>

      return (
        <Slot className={classNames} ref={ref} {...props}>
          {React.cloneElement(
            child,
            undefined,
            <>
              <SketchLayers tone={tone} />
              <span className="relative z-10 inline-flex items-center">
                {child.props.children}
              </span>
            </>,
          )}
        </Slot>
      )
    }

    const content = loading ? (
      <>
        {children}
        <span
          className="ml-2 size-5 shrink-0 animate-spin rounded-full border-2 border-current/30 border-t-current"
          aria-hidden
        />
      </>
    ) : (
      children
    )

    return (
      <button
        className={classNames}
        ref={ref}
        aria-busy={loading || undefined}
        {...props}
        disabled={props.disabled || loading}
      >
        {tone ? (
          <>
            <SketchLayers tone={tone} />
            <span className="relative z-10 inline-flex items-center">
              {content}
            </span>
          </>
        ) : (
          content
        )}
      </button>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
